import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Download } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DOCS = {
  terms: { title: 'Terms & Conditions', file: 'terms.pdf' },
  privacy: { title: 'Privacy & Data Safety Policy', file: 'privacy.pdf' },
  refund: { title: 'Refund Policy', file: 'refund.pdf' },
  'pause-90': { title: '90 Day Pause Policy', file: 'pause-90.pdf' },
  'pause-indefinite': { title: 'Indefinite Pause Policy', file: 'pause-indefinite.pdf' },
};

// Carrier-required SMS disclosures (A2P 10DLC / CTIA). Rendered as crawlable
// HTML above the PDF so campaign reviewers can verify them — PDFs in iframes
// are frequently flagged as "unverifiable website".
const SMS_SECTIONS = {
  privacy: (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-4 text-sm text-gray-700 space-y-3" data-testid="legal-sms-privacy-section">
      <h2 className="text-lg font-bold text-gray-900">SMS / Text Messaging Privacy</h2>
      <p>
        No mobile information will be shared with third parties or affiliates for marketing or
        promotional purposes. Information sharing with subcontractors in support services, such as
        customer service, is permitted. All other use-case categories exclude text-messaging
        originator opt-in data and consent; this information will not be shared with, or sold to,
        any third parties.
      </p>
      <p>
        When you opt in to SMS on our signup form, we record your phone number, the consent text
        displayed, the date and time, and your IP address solely as evidence of your consent. You
        can withdraw consent at any time by replying STOP to any message.
      </p>
    </div>
  ),
  terms: (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-4 text-sm text-gray-700 space-y-3" data-testid="legal-sms-terms-section">
      <h2 className="text-lg font-bold text-gray-900">SMS / Text Messaging Terms</h2>
      <p>
        By opting in via the checkbox on our signup form, you agree to receive transactional SMS
        from HIBFF LTD ("HiBFF"): account verification codes, security notifications,
        parental-consent requests, subscription reminders and progress updates. Message frequency
        varies. Message and data rates may apply.
      </p>
      <p>
        Reply <strong>STOP</strong> at any time to cancel and you will receive no further messages.
        Reply <strong>HELP</strong> for help, or contact us at support@hibff.com. Carriers are not
        liable for delayed or undelivered messages. Consent to SMS is not a condition of purchase.
      </p>
    </div>
  ),
};

export default function LegalDocPage({ doc }) {
  const meta = DOCS[doc];
  const url = `${API_URL}/api/static/docs/legal/${meta.file}`;
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 mb-2" data-testid="legal-back-home">
              <ArrowLeft size={14} /> Back to HiBFF
            </Link>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900">{meta.title}</h1>
            <p className="text-xs text-gray-500 mt-1">HIBFF LTD · Official document</p>
          </div>
          <div className="flex items-center gap-2">
            <a href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 border-gray-900 text-gray-900 text-sm font-bold hover:bg-gray-100"
              data-testid={`legal-open-${doc}`}>
              Open Full Screen
            </a>
            <a href={url} download className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gray-900 text-white text-sm font-bold hover:bg-gray-700"
              data-testid={`legal-download-${doc}`}>
              <Download size={14} /> Download PDF
            </a>
          </div>
        </div>
        {SMS_SECTIONS[doc] || null}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" style={{ height: '78vh' }}>
          <iframe title={meta.title} src={`${url}#toolbar=0`} className="w-full h-full" data-testid={`legal-viewer-${doc}`} />
        </div>
        <p className="text-[11px] text-gray-400 mt-3">
          If the document doesn't display on your device, use the Download PDF button above.
        </p>
      </div>
    </div>
  );
}
